import React, { useContext, useEffect } from 'react';
import { observer } from 'mobx-react-lite';

import { TradingContext } from '@trader/contexts';
import { TModalStore, TPositionMetricEntity, useMst } from '@trader/store';
import { ModalTitle, Progress, TradingIsClosed } from '@trader/components';
import { useI18next } from '@trader/services';
import { useBusinessLogic } from '@trader/hooks';

import {
  IPositionMetricStore,
  TradingModalHeader,
} from '../components/tradingModalHeader';
import { BuyButton } from '../components/buyButton';
import { Protection } from '../createOrder/components/Protection';

import * as Styled from './styled';
import { ConfirmButton } from '../components/confirmButton';
import { calculateSpread } from '@trader/utils';

export const AddProtection: React.FC = observer(() => {
  const store = useMst();
  const { translate } = useI18next();

  const {
    instrument: { general, market },
    tradingProtection: { isSwitchLoss, isSwitchProfit },
    realTimePrice,
    clearPreviousState,
    handleStartTrading,
    switchTakeProfit,
    switchStopLoss,
    errors,
    handleChangeProtection,
    toggleEditField,
  } = useContext(TradingContext);

  const { applyCondition: applyConditionThird } =
    useBusinessLogic('third_case');

  const modal: TModalStore = store.ui.modal;
  const positionId = modal.options.get('positionId');
  const symbol = modal.options.get('symbol');
  const type = modal.options.get('type');

  const metric =
    store.entities.positionsMetrics.get<TPositionMetricEntity>(positionId);

  const stopLossPrice = metric?.stopLoss?.stopPrice;
  const takeProfitPrice = metric?.takeProfit?.limitPrice;

  const getInstrumentDetails = async () => {
    if (symbol) {
      const res = await store.pages.trading.getInstrumentSpecificationAsync.run(
        symbol
      );
      res && handleStartTrading(res, type);
    }
  };

  const addProtection = () => {
    metric?.editPositionAsync.run({
      positionId,
      body: {
        stopPrice: market.protection.isSwitchLoss
          ? +market.protection.lossValue
          : 0,
        limitPrice: market.protection.isSwitchProfit
          ? +market.protection.profitValue
          : 0,
        symbol: general.symbol,
      },
    });
  };

  useEffect(() => {
    getInstrumentDetails();
    return () => {
      clearPreviousState();
    };
  }, [symbol]);

  useEffect(() => {
    if (metric?.stopLoss?.stopPrice) {
      handleChangeProtection(stopLossPrice, 'lossValue');
      switchStopLoss(true);
      toggleEditField('stopLoss');
    }
  }, [general.symbol, stopLossPrice]);

  useEffect(() => {
    if (metric?.takeProfit?.limitPrice) {
      handleChangeProtection(takeProfitPrice, 'profitValue');
      switchTakeProfit(true);
      toggleEditField('takeProfit');
    }
  }, [general.symbol, takeProfitPrice]);

  if (!general.symbol) {
    return (
      <Styled.Root>
        <Progress />
      </Styled.Root>
    );
  }

  if (applyConditionThird().status === 'failed') {
    return (
      <Styled.Root>
        <TradingIsClosed
          iconTypeName='warning'
          title={translate('WARNINGS.TRADING_IS_DISABLED')}
          content={translate('WARNINGS.PLEASE_CONTACT_SUPPORT', {
            email: import.meta.env.VITE_SUPPORT_EMAIL,
          })}
          buttonContent={translate('COMMON.LABELS.PROCEED')}
          onClick={applyConditionThird()?.action}
        />
      </Styled.Root>
    );
  }

  return (
    <Styled.Root>
      <ModalTitle title={translate('COMMON.LABELS.ADD_PROTECTION')} />
      {general?.tradingStatus !== 'CLOSE_ONLY' ? (
        <Styled.Position>
          <TradingModalHeader<IPositionMetricStore>
            type='position'
            metric={metric}
          />
          {!import.meta.env.VITE_IS_DMA && (
            <Styled.ActionButtons>
              <BuyButton isDisabled order='Market' isAsk type='Sell' />
              <Styled.Spread>
                {calculateSpread({
                  ask: Number(realTimePrice.ask),
                  bid: Number(realTimePrice.bid),
                  pipSize: general.pipSize,
                })}
              </Styled.Spread>
              <BuyButton isDisabled order='Market' type='Buy' />
            </Styled.ActionButtons>
          )}

          <Protection />
          <ConfirmButton
            isDisabled={
              (!isSwitchLoss && !isSwitchProfit) ||
              metric?.editPositionAsync.inProgress ||
              !!errors.stopLossError ||
              !!errors.takeProfitError
            }
            isLoading={metric?.editPositionAsync.inProgress}
            onClick={addProtection}
            title={translate('COMMON.LABELS.ADD_PROTECTION')}
          />
        </Styled.Position>
      ) : (
        <TradingIsClosed
          iconTypeName='warning'
          title={translate('WARNINGS.TRADING_LIMITED_TO_CLOSE_ONLY_POSITIONS')}
        />
      )}
    </Styled.Root>
  );
});
